// Labels in plain words and as a tree, and the exact delta a label change asks for. Pure, so
// the rail, the picker and the strip agree and it can be unit-tested. Trash and Spam are
// viewable, never editable; Drafts, Sent and Chat are Gmail's to set; INBOX is Archive / Move
// back to the inbox, never a checkbox (docs/design/inbox-views.md).
import type { LabelView, ThreadActionRequest } from "@shared/api";
import type { ListSearch } from "./listSearch";

export const SYSTEM_LABEL_NAMES: Readonly<Record<string, string>> = {
  INBOX: "Inbox",
  STARRED: "Starred",
  UNREAD: "Unread",
  IMPORTANT: "Important",
  SENT: "Sent",
  DRAFT: "Drafts",
  SPAM: "Spam",
  TRASH: "Trash",
  CHAT: "Chat",
  CATEGORY_PERSONAL: "Personal",
  CATEGORY_SOCIAL: "Social",
  CATEGORY_PROMOTIONS: "Promotions",
  CATEGORY_UPDATES: "Updates",
  CATEGORY_FORUMS: "Forums",
};

/** Hidden threads: shown on request, never touched by an action. */
export const VIEW_ONLY_LABELS: ReadonlySet<string> = new Set(["TRASH", "SPAM"]);
/** Gmail state that is not a choice. */
export const GMAIL_OWNED_LABELS: ReadonlySet<string> = new Set(["DRAFT", "SENT", "CHAT"]);

/** A label id in words: the plain word for a system label, the cached name for a user label. */
export function labelName(id: string, lookup?: (id: string) => LabelView | undefined): string {
  const system = SYSTEM_LABEL_NAMES[id];
  if (system) return system;
  const label = lookup?.(id);
  return label ? label.name : id;
}

export function isViewOnlyLabel(id: string | undefined): boolean {
  return !!id && VIEW_ONLY_LABELS.has(id);
}

/** One row of the rail: a split the list can show. */
export interface RailRow {
  key: string;
  name: string;
  search: Pick<ListSearch, "view" | "label">;
  viewOnly?: boolean;
}

export const QUICK_SPLITS: readonly RailRow[] = [
  { key: "inbox", name: "Inbox", search: {} },
  // Unread threads waiting in the inbox (a part of Inbox, never a count).
  { key: "unread", name: "Unread", search: { view: "unread" } },
  { key: "starred", name: "Starred", search: { view: "starred" } },
  { key: "all", name: "All mail", search: { view: "all" } },
];

export const GMAIL_SPLITS: readonly RailRow[] = [
  { key: "IMPORTANT", name: "Important", search: { label: "IMPORTANT" } },
  { key: "SENT", name: "Sent", search: { label: "SENT" } },
  { key: "DRAFT", name: "Drafts", search: { label: "DRAFT" } },
  { key: "SPAM", name: "Spam", search: { label: "SPAM" }, viewOnly: true },
  { key: "TRASH", name: "Trash", search: { label: "TRASH" }, viewOnly: true },
];

/** Whether a rail row is the list on screen. */
export function rowActive(row: RailRow, search: ListSearch): boolean {
  if (search.q) return false;
  if (row.search.label) return search.label === row.search.label;
  return !search.label && (search.view ?? undefined) === row.search.view;
}

export interface LabelNode {
  /** The full name ("Clients/Acme"); the key of the node. */
  name: string;
  /** The last segment, shown in the tree. */
  leaf: string;
  depth: number;
  /** Null for a parent Gmail never listed as a label of its own (a name, not a row). */
  label: LabelView | null;
  children: LabelNode[];
}

/** User labels as a tree by `path`; parents come before children, siblings keep the input order. */
export function buildLabelTree(labels: readonly LabelView[]): LabelNode[] {
  const roots: LabelNode[] = [];
  const byName = new Map<string, LabelNode>();
  const ensure = (path: readonly string[]): LabelNode => {
    const name = path.join("/");
    const hit = byName.get(name);
    if (hit) return hit;
    const node: LabelNode = {
      name,
      leaf: path[path.length - 1] ?? name,
      depth: path.length - 1,
      label: null,
      children: [],
    };
    byName.set(name, node);
    if (path.length > 1) ensure(path.slice(0, -1)).children.push(node);
    else roots.push(node);
    return node;
  };
  for (const label of labels) {
    if (label.type !== "user") continue;
    const node = ensure(label.path.length ? label.path : [label.name]);
    node.label = label;
  }
  return roots;
}

/** Depth-first order for rendering. */
export function flattenTree(nodes: readonly LabelNode[]): LabelNode[] {
  const out: LabelNode[] = [];
  const walk = (list: readonly LabelNode[]) => {
    for (const n of list) {
      out.push(n);
      walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

/** Nodes whose name (or a descendant's) matches the filter, in tree order. */
export function filterTree(nodes: readonly LabelNode[], text: string): LabelNode[] {
  const needle = text.trim().toLowerCase();
  if (!needle) return flattenTree(nodes);
  const out: LabelNode[] = [];
  const walk = (list: readonly LabelNode[]): boolean => {
    let any = false;
    for (const n of list) {
      const self = n.name.toLowerCase().includes(needle);
      const start = out.length;
      out.push(n);
      const below = walk(n.children);
      if (!self && !below) out.length = start;
      else any = true;
    }
    return any;
  };
  walk(nodes);
  return out;
}

/** The system labels a picker may toggle (reversible, never hidden or Gmail-owned). */
export function settableSystemLabels(labels: readonly LabelView[]): LabelView[] {
  return labels.filter(
    (l) =>
      l.type === "system" &&
      l.id !== "INBOX" &&
      !VIEW_ONLY_LABELS.has(l.id) &&
      !GMAIL_OWNED_LABELS.has(l.id),
  );
}

/** The exact delta between what the thread has and what the boxes say, over the boxes shown. */
export function labelDelta(
  current: readonly string[],
  checked: ReadonlySet<string>,
  candidates: readonly string[],
): { addLabelIds: string[]; removeLabelIds: string[] } {
  const has = new Set(current);
  const addLabelIds = candidates.filter((id) => checked.has(id) && !has.has(id));
  const removeLabelIds = candidates.filter((id) => !checked.has(id) && has.has(id));
  return { addLabelIds, removeLabelIds };
}

/** A delta as a request; null when nothing would change. INBOX alone is an archive/unarchive. */
export function requestForDelta(delta: {
  addLabelIds: readonly string[];
  removeLabelIds: readonly string[];
}): ThreadActionRequest | null {
  const add = [...delta.addLabelIds];
  const remove = [...delta.removeLabelIds];
  if (!add.length && !remove.length) return null;
  if (!add.length && remove.length === 1 && remove[0] === "INBOX")
    return { kind: "gmail.archive_thread" };
  if (!remove.length && add.length === 1 && add[0] === "INBOX")
    return { kind: "gmail.unarchive_thread" };
  return { kind: "gmail.label", addLabelIds: add, removeLabelIds: remove };
}

/** `e` on a thread: archive when it is in the inbox, else move it back. */
export function archiveRequest(thread: { inInbox: boolean }): ThreadActionRequest {
  return { kind: thread.inInbox ? "gmail.archive_thread" : "gmail.unarchive_thread" };
}

export interface RequestWords {
  /** The button and the heading ("Archive"). */
  verb: string;
  /** While the work runs ("Archiving"). */
  doing: string;
  /** Once Gmail confirmed it ("Archived"). */
  done: string;
  /** The key that armed it and confirms it when pressed again, if any. */
  againKey: string | null;
}

export function requestWords(request: ThreadActionRequest): RequestWords {
  switch (request.kind) {
    case "gmail.archive_thread":
      return { verb: "Archive", doing: "Archiving", done: "Archived", againKey: "E" };
    case "gmail.unarchive_thread":
      return {
        verb: "Move back to the inbox",
        doing: "Moving back to the inbox",
        done: "Moved back to the inbox",
        againKey: "E",
      };
    case "gmail.label": {
      const n = (request.addLabelIds?.length ?? 0) + (request.removeLabelIds?.length ?? 0);
      return {
        verb: n === 1 ? "Change the label" : "Change labels",
        doing: "Changing labels",
        done: "Labels changed",
        againKey: null,
      };
    }
  }
}

/** Would this action take the thread out of the list on screen? (Hidden at once, with undo.) */
export function hidesFrom(request: ThreadActionRequest, search: ListSearch): boolean {
  if (search.q) return false;
  const removes = (id: string) =>
    request.kind === "gmail.archive_thread"
      ? id === "INBOX"
      : request.kind === "gmail.label"
        ? (request.removeLabelIds ?? []).includes(id)
        : false;
  if (search.label) return removes(search.label);
  switch (search.view) {
    case "unread":
      return removes("UNREAD");
    case "starred":
      return removes("STARRED");
    case "all":
      return false;
    default:
      return removes("INBOX");
  }
}

/** Would this action bring the thread into the list on screen? (Shown at once when it lands.) */
export function returnsTo(request: ThreadActionRequest, search: ListSearch): boolean {
  if (search.q) return false;
  const adds = (id: string) =>
    request.kind === "gmail.unarchive_thread"
      ? id === "INBOX"
      : request.kind === "gmail.label"
        ? (request.addLabelIds ?? []).includes(id)
        : false;
  if (search.label) return adds(search.label);
  switch (search.view) {
    case "unread":
      return adds("UNREAD");
    case "starred":
      return adds("STARRED");
    case "all":
      return false;
    default:
      return adds("INBOX");
  }
}

/** The server's refusal reasons (400 invalid_request) in words. */
export function refusalWords(reason: string | null, message: string | null): string {
  switch (reason) {
    case "use_archive":
      return "Taking a thread out of the inbox is Archive; use that button.";
    case "use_unarchive":
      return "Putting a thread back in the inbox is Move back to the inbox; use that button.";
    case "hidden_label":
      return "Trash and Spam stay in Gmail's hands; MeeMail never moves mail there or back.";
    case "immutable_label":
      return "Drafts, Sent and Chat are Gmail's to set.";
    case "label_not_found":
      return "That label is not in this account any more. Reopen the picker to see the list.";
    case "empty_delta":
      return "Nothing would change.";
    case "overlap":
      return "A label cannot be both added and removed.";
    case "unexpected_delta":
      return "Archive and Move back take no labels.";
    default:
      return message ?? "The server did not accept this change. Nothing happened in Gmail.";
  }
}
